import { Link } from "@tanstack/react-router";
import { Announcement03, ArrowUpRight, InfoCircle, MinusCircle, ShieldZap, VideoRecorder } from "@untitledui/icons";
import { AlertTriangle, Camera, MoreVertical, ShieldOff } from "lucide-react";
import { useState } from "react";
import { useVideoCollaborations } from "@/api/collaborations";
import { useProductSafetyAnalysis } from "@/api/product";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { AddToCampaignMenuItem } from "@/components/assurance/add-to-campaign-menu-item";
import { DetailedVideoStatusBadge } from "@/components/assurance/badges/video-status-badge";
import { Badge } from "@/components/base/badges/badges";
import { RemoveCreatorFromCollabModal } from "@/components/remove-creator-from-collab-modal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ManageCampaignsModal } from "@/pages/creators/tabs/manage-campaigns-modal";
import { useMerchant } from "@/providers/MerchantProvider";
import { legacyRisksToReviewRoute, videoExplorerRoute } from "@/router";
import { formatCreatorId } from "@/utils/formatters";

type SafetyTabProps = {
  currencyFormatter: Intl.NumberFormat;
  productId: string;
  productName: string;
};

const numFormatter = new Intl.NumberFormat("en-US", {
  compactDisplay: "short",
  notation: "compact",
});

const getOrdinalSuffix = (day: number) => {
  if (day > 3 && day < 21) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

const formatVideoDate = (date: string) => {
  const d = new Date(date);
  const day = d.getDate();
  const month = d.toLocaleDateString("en-US", { month: "short" });
  const year = d.getFullYear();
  const hours = d.getHours().toString().padStart(2, "0");
  const minutes = d.getMinutes().toString().padStart(2, "0");
  return `${day}${getOrdinalSuffix(day)} ${month} ${year} @${hours}:${minutes}`;
};

export const SafetyTab = ({ currencyFormatter, productId, productName }: SafetyTabProps) => {
  const { data: safetyData, isLoading } = useProductSafetyAnalysis(productId);
  const [manageCampaignVideoId, setManageCampaignVideoId] = useState<string | null>(null);
  const [removeCollabVideoId, setRemoveCollabVideoId] = useState<string | null>(null);
  const merchant = useMerchant();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <LoadingIndicator />
      </div>
    );
  }

  if (!safetyData || safetyData.riskOverview.totalVideos === 0) {
    return (
      <div className="w-full h-[400px] bg-gray-50 rounded-lg flex flex-col items-center justify-center text-center mt-8">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
          style={{ backgroundColor: "#F5F5F5" }}
        >
          <ShieldOff className="w-6 h-6" style={{ color: "#717680" }} />
        </div>
        <h4 className="text-sm font-medium text-primary mb-1">Safety analysis unavailable</h4>
        <p className="text-sm text-tertiary max-w-md">
          More content is needed before we can run this <br /> analysis. This will auto generate as creators post.
        </p>
      </div>
    );
  }

  const { riskOverview, mostCommonViolations, videosWithRisks } = safetyData;

  return (
    <div className="space-y-8 mt-8">
      {/* First Section: Two Column Layout */}
      <div className="flex gap-24">
        {/* Left Column: Risk Overview Header */}
        <div className="w-[150px] flex-shrink-0">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-5 h-5 text-tertiary" />
            <h3 className="text-sm font-medium text-secondary">Risk overview</h3>
          </div>
          <p className="text-xs text-tertiary leading-relaxed">Brand safety concerns attached to your product</p>
        </div>

        {/* Right Column: Stats Table + Most Common Violations */}
        <div className="flex-1 space-y-12">
          {/* Stats Table */}
          <div className="space-y-0">
            <div className="flex items-center justify-between py-3 border-b border-border-primary">
              <span className="text-sm text-tertiary">Videos deemed safe</span>
              <div className="flex items-center gap-1.5 font-light">
                <span className="text-sm text-success-600">{riskOverview.videosDeemedSafe}</span>
                <span className="text-sm text-tertiary">({riskOverview.percentageSafe}%)</span>
                <InfoCircle className="w-4 h-4 text-tertiary" />
              </div>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-border-primary">
              <span className="text-sm text-tertiary">Videos with active risks</span>
              <div className="flex items-center gap-1.5 font-light">
                <span className="text-sm text-error-600">{riskOverview.videosWithActiveRisks}</span>
                <span className="text-sm text-tertiary">({riskOverview.percentageActiveRisks}%)</span>
                <InfoCircle className="w-4 h-4 text-tertiary" />
              </div>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-border-primary">
              <span className="text-sm text-tertiary">Videos with risks awaiting review</span>
              <div className="flex items-center gap-1.5 font-light">
                <span className="text-sm text-warning-600">{riskOverview.videosWithRisksAwaitingReview}</span>
                <span className="text-sm text-tertiary">({riskOverview.percentageAwaitingReview}%)</span>
                <InfoCircle className="w-4 h-4 text-tertiary" />
              </div>
            </div>
          </div>

          {/* Most Common Violations */}
          {mostCommonViolations.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-secondary">Most common violations</h3>
              <div className="flex flex-wrap gap-2">
                {mostCommonViolations.map((violation) => (
                  <Badge key={violation.flagType} color="gray">
                    {violation.flagType}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Videos with risks */}
      {videosWithRisks.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <ShieldZap className="w-5 h-5 text-tertiary" />
            <h3 className="text-sm font-medium text-secondary">Videos with risks</h3>
          </div>
          <div className="space-y-4">
            {videosWithRisks.map((video) => (
              <div
                key={video.videoId}
                className="border border-border-primary rounded-lg overflow-hidden hover:border-gray-300 transition-colors"
              >
                <div className="flex">
                  {/* Video Player or Thumbnail */}
                  <div className="relative w-32 flex-shrink-0 bg-black group">
                    {video.videoUrl ? (
                      <>
                        <img
                          src={video.urlFirstFrame || undefined}
                          alt=""
                          className="h-full w-full object-cover aspect-[9/16]"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const videoEl = document.getElementById(`video-${video.videoId}`) as HTMLVideoElement;
                            if (videoEl) {
                              videoEl.play();
                              videoEl.style.display = "block";
                              const imgEl = videoEl.previousElementSibling as HTMLImageElement;
                              if (imgEl) imgEl.style.display = "none";
                              const btnEl = videoEl.nextElementSibling as HTMLElement;
                              if (btnEl) btnEl.style.display = "none";
                            }
                          }}
                          className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                            <div className="w-0 h-0 border-t-[8px] border-t-transparent border-l-[14px] border-l-black border-b-[8px] border-b-transparent ml-1" />
                          </div>
                        </button>
                        <video
                          id={`video-${video.videoId}`}
                          src={video.videoUrl}
                          controls
                          className="h-full object-cover aspect-[9/16] hidden"
                          preload="metadata"
                        />
                      </>
                    ) : video.urlFirstFrame ? (
                      <img src={video.urlFirstFrame} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                        <Camera className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                  </div>

                  {/* Right Column - Split into 2 sections */}
                  <div className="flex-1 flex flex-col min-w-0">
                    {/* Section 1: Info and Menu (with padding) */}
                    <div className="p-4 flex-1">
                      {/* Title, Date, Status Tag and Menu */}
                      <div className="space-y-0.5 mb-3">
                        <div className="flex items-start gap-2">
                          {/* Clickable Title with dotted underline - truncated */}
                          <div className="flex-1 min-w-0">
                            <Link
                              to={videoExplorerRoute.to}
                              params={{ merchantId: merchant }}
                              search={{ video_id: video.videoId }}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-medium text-primary underline decoration-dotted hover:text-primary/80 text-left line-clamp-1"
                            >
                              {video.videoTitle || "Untitled Video"}
                            </Link>
                            <p className="text-xs text-tertiary mt-0.5">{formatVideoDate(video.publishDate)}</p>
                          </div>
                          {/* Status Badge */}
                          <DetailedVideoStatusBadge status={video.safetyStatus} showInfoIcon={true} />
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                type="button"
                                className="p-1.5 hover:bg-gray-100 rounded flex-shrink-0 transition-colors"
                                aria-label="Video actions"
                              >
                                <MoreVertical className="w-4 h-4 text-gray-600" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" sideOffset={5}>
                              <DropdownMenuItem asChild>
                                <Link
                                  to={videoExplorerRoute.to}
                                  params={{ merchantId: merchant }}
                                  search={{ video_id: video.videoId }}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="cursor-pointer flex gap-2"
                                >
                                  <VideoRecorder className="w-4 h-4 text-tertiary" />
                                  See Video in Detail
                                </Link>
                              </DropdownMenuItem>
                              {video.isInAdCampaign ? (
                                <DropdownMenuItem
                                  onSelect={() => {
                                    setManageCampaignVideoId(video.videoId);
                                  }}
                                >
                                  <MinusCircle className="w-4 h-4 text-tertiary" />
                                  Remove From Ads campaign
                                </DropdownMenuItem>
                              ) : (
                                !video.isExcludedFromAdCampaign && !video.isQuarantined && <AddToCampaignMenuItem />
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onSelect={() => setRemoveCollabVideoId(video.videoId)}
                                disabled={!video.creatorId}
                                className={!video.creatorId ? "text-gray-400" : "flex gap-2"}
                              >
                                <Announcement03 className="w-4 h-4 text-tertiary" />
                                Remove creator from collab
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>

                      {/* Risks Identified Section */}
                      {video.flagTypes.length > 0 && (
                        <div className="space-y-1.5">
                          <p className="text-xs text-tertiary font-medium">Risks Identified</p>
                          <div className="flex flex-wrap gap-1.5">
                            {video.flagTypes.map((flagType, idx) => (
                              <Badge key={`${flagType}-${idx}`} color="gray" size="sm">
                                {flagType}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Section 2: Stats */}
                    <div>
                      <div className="border-t border-border-primary" />
                      <div className="grid grid-cols-4 gap-4 p-4">
                        <div>
                          <div className="text-xs text-tertiary mb-1">GMV</div>
                          <div className="text-sm font-medium text-primary">{currencyFormatter.format(video.gmv)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-tertiary mb-1">Views</div>
                          <div className="text-sm font-medium text-primary">{numFormatter.format(video.views)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-tertiary mb-1">Likes</div>
                          <div className="text-sm font-medium text-primary">-</div>
                        </div>
                        <div>
                          <div className="text-xs text-tertiary mb-1">Sales</div>
                          <div className="text-sm font-medium text-primary">{numFormatter.format(video.unitSales)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* See all videos with risks link */}
          <Link
            to={legacyRisksToReviewRoute.to}
            params={{ merchantId: merchant }}
            search={{ tab: "pendingManualReview", product: [{ id: productId, label: productName }] }}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-sm text-secondary hover:text-primary font-medium transition-colors"
          >
            See all videos for this product with risks
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      )}

      {/* Remove Creator from Collab Modal */}
      {safetyData?.videosWithRisks && (
        <RemoveCreatorFromCollabModalWrapper
          videoId={removeCollabVideoId}
          videos={safetyData.videosWithRisks}
          onClose={() => setRemoveCollabVideoId(null)}
        />
      )}

      {/* Manage Campaigns Modal */}
      {manageCampaignVideoId && (
        <ManageCampaignsModal
          videoId={manageCampaignVideoId}
          open={!!manageCampaignVideoId}
          onOpenChange={(open) => {
            if (!open) setManageCampaignVideoId(null);
          }}
        />
      )}
    </div>
  );
};

type RemoveCreatorFromCollabModalWrapperProps = {
  videoId: string | null;
  videos: Array<{
    videoId: string;
    creatorId: string | null;
    creatorThumbnail?: string | null;
    [key: string]: any;
  }>;
  onClose: () => void;
};

const RemoveCreatorFromCollabModalWrapper = ({
  videoId,
  videos,
  onClose,
}: RemoveCreatorFromCollabModalWrapperProps) => {
  const { data: collaborationsData } = useVideoCollaborations(
    videoId ? { mode: "include", videoIds: [videoId] } : null,
  );

  if (!videoId) return null;

  const video = videos.find((v) => v.videoId === videoId);
  if (!video || !video.creatorId) return null;

  const targetCollaborations =
    collaborationsData?.targetCollaborations.map((collab) => ({
      collaborationId: collab.collaborationId,
      name: collab.name,
      status: "Active", // Target collaborations are always active
    })) || [];

  return (
    <RemoveCreatorFromCollabModal
      creator={{
        creatorId: video.creatorId,
        creatorName: formatCreatorId(video.creatorId),
        thumbnail: video.creatorThumbnail,
        targetCollaborations,
      }}
      open={!!videoId}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      onSuccess={onClose}
    />
  );
};
